import { Languages } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { buildLocalizedPath, useLang, type Lang } from "@/contexts/LanguageContext";

interface Props {
  /** "compact" = small icon button. "full" = pill with FR | EN */
  variant?: "compact" | "full";
  className?: string;
}

export const LangSwitcher = ({ variant = "full", className = "" }: Props) => {
  const { lang, setLang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();

  const switchTo = (target: Lang) => {
    if (target === lang) return;
    setLang(target);
    const next = buildLocalizedPath(location.pathname, target) + location.search + location.hash;
    navigate(next);
  };

  if (variant === "compact") {
    const next: Lang = lang === "fr" ? "en" : "fr";
    return (
      <button
        type="button"
        onClick={() => switchTo(next)}
        aria-label={lang === "fr" ? "Switch to English" : "Passer en français"}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-body font-bold uppercase tracking-wider border border-primary/30 text-primary hover:bg-primary/10 transition-colors ${className}`}
      >
        <Languages className="w-3.5 h-3.5" />
        {next.toUpperCase()}
      </button>
    );
  }

  return (
    <div
      role="group"
      aria-label="Language selector"
      className={`inline-flex items-center rounded-full border border-primary/25 bg-background/60 backdrop-blur-sm p-0.5 ${className}`}
    >
      {(["fr", "en"] as const).map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => switchTo(l)}
          aria-pressed={lang === l}
          className={`px-2.5 py-1 rounded-full text-[11px] font-body font-bold tracking-wider transition-all ${
            lang === l
              ? "bg-primary text-primary-foreground shadow-sm"
              : "text-muted-foreground hover:text-primary"
          }`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
};
